import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, MessageCircle, Search, Zap } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { api } from "../../api";
import { SHELL } from "../../constants/testIds";
import { isPhoneQuery } from "../../lib/format";
import { toast } from "../ui/sonner";

// DEFECT-D-18: keywords that surface a "quick action" hint above the member results — typing
// "pay"/"checkin"/"renew"/"message" is a command, not just a name to search for.
const ACTION_KEYWORDS = [
  { match: /pay|payment/i, label: "Take a payment", hint: "Find a member below, then use the payment icon" },
  { match: /check\s?in/i, label: "Check in a member", hint: "Find a member below, then use the check-in icon" },
  { match: /renew/i, label: "Renew a membership", hint: "Find a member below, then use the payment icon" },
  { match: /message|msg/i, label: "Message a member", hint: "Find a member below, then use the message icon" },
];

// EL-013 member lookup: debounced search-as-you-type against the real /members endpoint —
// detects a phone-looking query (digits/+/-/space) and searches `phone` (partial match,
// backend-side regex) instead of `name`, so staff can find a member by typing digits of their
// phone number just as reliably as by name. Clicking (or arrowing-to + Enter-ing) a result opens
// that member's full detail page directly — it used to just scroll/highlight a row on the list,
// and left the results dropdown hanging open over whatever page it navigated to since nothing
// ever closed it.
// Shared verbatim by the Owner Cockpit's glass `TopBar` (light/dark theme, `/app/members`) and
// the Desk shell's solid dark header (`/desk/members`) — `variant`/`basePath`/`testId` let each
// host style + route it correctly without forking the search logic.
export function GlobalSearch({ basePath = "/app/members", testId = SHELL.searchInput, variant = "light" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const isDark = variant === "dark";

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const trimmed = query.trim();
        const params = isPhoneQuery(trimmed) ? { phone: trimmed.replace(/[\s-]/g, "") } : { name: trimmed };
        const { data } = await api.get("/members", { params: { ...params, limit: 6 } });
        setResults(data.members);
        setActiveIndex(0);
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);
  const selectResult = (m) => {
    navigate(`${basePath}/${m.id}`);
    setOpen(false);
    setQuery("");
    setResults([]);
  };

  // DEFECT-D-18: one-click check-in straight from the search dropdown — no navigation at all.
  const quickCheckin = async (e, m) => {
    e.stopPropagation();
    try {
      await api.post("/checkin", { branch_id: m.branch_id, method: "manual", member_ref: m.id });
      toast.success(`${m.profile.name} checked in.`);
      setOpen(false);
      setQuery("");
      setResults([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Check-in failed.");
    }
  };

  const quickRenew = (e, m) => {
    e.stopPropagation();
    navigate(`${basePath}/${m.id}?renew=1`);
    setOpen(false); setQuery(""); setResults([]);
  };

  const quickMessage = (e, m) => {
    e.stopPropagation();
    const digits = (m.profile.phone || "").replace(/[^\d+]/g, "");
    window.open(`https://wa.me/${digits.replace(/^\+/, "")}`, "_blank");
    setOpen(false); setQuery(""); setResults([]);
  };

  const matchedAction = ACTION_KEYWORDS.find((a) => a.match.test(query.trim()));

  const handleKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectResult(results[activeIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative flex-1 max-w-md">
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-[10px] transition-all duration-150 ${isDark ? "bg-white/10 focus-within:bg-white/20" : "focus-within:bg-white/40"}`}
      >
        <Search size={16} strokeWidth={1.7} style={{ color: isDark ? "rgba(255,255,255,0.5)" : "var(--scarlet-text-secondary)" }} />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Search members… (press Enter to open)"
          className={`scarlet-focusable bg-transparent outline-none text-sm w-full ${isDark ? "placeholder:text-white/40 text-white" : "placeholder:text-[var(--scarlet-text-secondary)]"}`}
          style={isDark ? undefined : { color: "var(--scarlet-text)" }}
        />
      </div>
      <AnimatePresence>
        {open && (results.length > 0 || matchedAction) && (
          <motion.div
            initial={reducedMotion ? { opacity: 1, scale: 1, y: 0 } : { opacity: 0, scale: 0.98, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reducedMotion ? { opacity: 1 } : { opacity: 0, scale: 0.98, y: -4 }}
            transition={reducedMotion ? { duration: 0 } : { duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className={isDark ? "absolute top-full mt-2 left-0 right-0 rounded-[14px] p-2 z-50" : "glass-dropdown absolute top-full mt-2 left-0 right-0 rounded-[14px] p-2 z-50"}
            style={isDark ? { background: "#1E1E22", border: "1px solid rgba(255,255,255,0.08)" } : undefined}
            data-testid="global-search-results"
          >
            {matchedAction && (
              <div data-testid={SHELL.searchQuickActionHint} className="px-3 py-2 rounded-[8px] text-xs flex items-center gap-1.5 mb-1" style={{ background: isDark ? "rgba(214, 32, 43,0.14)" : "var(--scarlet-accent-tint)", color: isDark ? "#FFB4B4" : "var(--scarlet-accent)" }}>
                <Zap size={12} /> <strong>{matchedAction.label}</strong> — {matchedAction.hint}
              </div>
            )}
            {results.map((m, i) => (
              <div
                key={m.id}
                data-testid={`search-result-${m.id}`}
                onMouseEnter={() => setActiveIndex(i)}
                className={`w-full text-left px-3 py-2 rounded-[8px] text-sm flex items-center justify-between gap-2 scarlet-focusable ${isDark ? "hover:bg-white/10" : "hover:bg-black/5"}`}
                style={i === activeIndex ? { background: isDark ? "rgba(214, 32, 43,0.18)" : "var(--scarlet-accent-tint)" } : undefined}
              >
                <button onClick={() => selectResult(m)} className="flex-1 min-w-0 flex items-center justify-between text-left">
                  <span style={{ color: isDark ? "#fff" : "var(--scarlet-text)" }}>{m.profile.name}</span>
                  <span className="tabular-nums-force ml-2" style={{ color: isDark ? "rgba(255,255,255,0.5)" : "var(--scarlet-text-secondary)" }}>{m.profile.phone}</span>
                </button>
                {/* DEFECT-D-18: direct action icons — fewer steps than navigating to the member's full profile first. */}
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={SHELL.searchQuickCheckin(m.id)} title="Check in" onClick={(e) => quickCheckin(e, m)} className={`p-1.5 rounded-full transition-colors ${isDark ? "hover:bg-white/15" : "hover:bg-black/10"}`}>
                    <Zap size={13} style={{ color: isDark ? "rgba(255,255,255,0.7)" : "var(--scarlet-text-secondary)" }} />
                  </button>
                  <button data-testid={SHELL.searchQuickRenew(m.id)} title="Renew / take payment" onClick={(e) => quickRenew(e, m)} className={`p-1.5 rounded-full transition-colors ${isDark ? "hover:bg-white/15" : "hover:bg-black/10"}`}>
                    <CreditCard size={13} style={{ color: isDark ? "rgba(255,255,255,0.7)" : "var(--scarlet-text-secondary)" }} />
                  </button>
                  <button data-testid={SHELL.searchQuickMessage(m.id)} title="Message on WhatsApp" onClick={(e) => quickMessage(e, m)} className={`p-1.5 rounded-full transition-colors ${isDark ? "hover:bg-white/15" : "hover:bg-black/10"}`}>
                    <MessageCircle size={13} style={{ color: isDark ? "rgba(255,255,255,0.7)" : "var(--scarlet-text-secondary)" }} />
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
